/**
 * Public patient-intake payment step helpers.
 *
 * The booking fee is taken as soon as the patient picks a slot, so the only thing that has to
 * survive the full page redirect to Stripe is the receipt — parked in `sessionStorage` per note
 * id, because router state does not come back from a redirect. The questionnaire that follows
 * happens entirely inside the SPA.
 */

/** `sessionStorage` key holding the paid payment, so `/finish` can show the receipt. */
export const getPatientIntakePaymentReceiptStorageKey = (noteId = "") =>
	`patient-intake-payment-receipt:${noteId || ""}`;

const readJsonFromSessionStorage = (key) => {
	try {
		const raw = window.sessionStorage.getItem(key);
		return raw ? JSON.parse(raw) : null;
	} catch (_error) {
		return null;
	}
};

const writeJsonToSessionStorage = (key, value) => {
	try {
		window.sessionStorage.setItem(key, JSON.stringify(value));
		return true;
	} catch (_error) {
		return false;
	}
};

const removeFromSessionStorage = (key) => {
	try {
		window.sessionStorage.removeItem(key);
		return true;
	} catch (_error) {
		return false;
	}
};

/** Parks the paid payment, reporting whether it landed — see `savePatientIntakePaymentCompletion`. */
export const savePatientIntakePaymentReceipt = ({ noteId = "", payment = {} }) =>
	writeJsonToSessionStorage(getPatientIntakePaymentReceiptStorageKey(noteId), payment);

export const readPatientIntakePaymentReceipt = ({ noteId = "" }) =>
	readJsonFromSessionStorage(getPatientIntakePaymentReceiptStorageKey(noteId));

/**
 * The receipt is a one-time confirmation for the booking that was just completed, so `/finish`
 * drops it as soon as it has been rendered — otherwise coming back to that URL days later would
 * re-announce a payment that belongs to a booking the patient has long since finished.
 */
export const clearPatientIntakePaymentReceipt = ({ noteId = "" }) =>
	removeFromSessionStorage(getPatientIntakePaymentReceiptStorageKey(noteId));

/** `75` + `CAD` -> `75.00 CAD`. */
export const formatPatientIntakePaymentAmount = ({ amount = 0, currency = "" }) => {
	const number = Number.parseFloat(amount);
	const formattedAmount = (Number.isFinite(number) ? number : 0).toFixed(2);

	return currency ? `${formattedAmount} ${String(currency).toUpperCase()}` : formattedAmount;
};

/** The order summary shown before any coupon is applied. */
export const buildPatientIntakePaymentSummary = ({ payment = {} }) => ({
	subtotal: Number.parseFloat(payment?.amount) || 0,
	discount: 0,
	total: Number.parseFloat(payment?.amount) || 0,
	currency: payment?.currency || "",
	label: payment?.label || "",
});

/**
 * The booking-fee endpoints are public and unauthenticated, so the backend has no patient locale
 * to answer in and always replies in English. Every message it can send is mapped back to the key
 * it was written from, so the patient reads it in their own language; anything unrecognised is
 * shown as it arrived rather than hidden behind a generic sentence.
 */
const patientIntakePaymentMessageKeys = {
	"this discount code is not valid.": "system_messages.discount_code_could_not_be_applied",
	"this discount code is no longer available.": "system_messages.discount_code_is_no_longer_available",
	"this clinic is not currently collecting booking fees.": "system_messages.clinic_is_not_collecting_booking_fees",
	"no booking fee is configured for this clinic.": "system_messages.clinic_is_not_collecting_booking_fees",
	"this booking could not be found.": "system_messages.booking_could_not_be_found",
	"this booking is already being set up for payment. please try again in a moment.":
		"system_messages.payment_is_already_being_set_up",
	"we could not close your previous payment page. please try again in a moment.":
		"system_messages.payment_is_already_being_set_up",
	"we could not start the payment. please try again.": "system_messages.payment_could_not_be_started",
};

export const translatePatientIntakePaymentMessage = ({ message = "", fallbackKey = "", t }) => {
	const serverMessage = String(message || "").trim();
	const messageKey = patientIntakePaymentMessageKeys[serverMessage.toLowerCase()] || "";

	if (messageKey) return t(messageKey);

	return serverMessage || (fallbackKey ? t(fallbackKey) : "");
};

/**
 * The intake step a freshly created note goes to.
 *
 * Clinics that collect no clinical questionnaire skip `/cases` and `/questions`
 * entirely and go straight to the patient's details. Every entry point that
 * creates a note routes through here so they cannot drift apart.
 */
export const getPatientIntakeStepAfterNoteCreated = ({ patientIntakeData = {} } = {}) =>
	patientIntakeData?.skip_assessment ? "patient" : "cases";

/**
 * The step a freshly created booking goes to.
 *
 * A clinic charging a booking fee asks for the patient's details first, so the
 * terms are accepted before any money moves; the fee is taken next and the
 * clinical questionnaire follows it. A clinic charging nothing is untouched and
 * goes straight into the questionnaire as it always has.
 */
export const getPatientIntakeStepAfterSlotChosen = ({ patientIntakeData = {} } = {}) =>
	patientIntakeData?.payment?.required ? "patient" : getPatientIntakeStepAfterNoteCreated({ patientIntakeData });

/**
 * Where a patient goes once the fee has settled.
 *
 * Their details are already in, so what is left is the clinical questionnaire —
 * and the step that ends it is the one that completes the intake. A clinic that
 * collects no questionnaire has nothing left to ask, so settling is the end of
 * the flow and the booking is completed on the way to it.
 */
export const getPatientIntakeStepAfterPaid = ({ patientIntakeData = {} } = {}) =>
	patientIntakeData?.skip_assessment ? "finish" : "cases";

/**
 * Whether this step is the one that completes the intake.
 *
 * Completion has to land after the fee is settled, so it moves with the flow:
 * normally the details step is last and completes, but where a fee is charged
 * the details come before the payment and the questionnaire ends up last.
 */
export const isPatientIntakeCompletedAtQuestions = ({ patientIntakeData = {} } = {}) =>
	Boolean(patientIntakeData?.payment?.required) && !patientIntakeData?.skip_assessment;

/**
 * What a booking's fee state should say on a list, or `""` when it should say nothing.
 *
 * `is_paid` on its own cannot be shown, because false covers two different
 * bookings: the one nobody paid for, and the one that never cost anything.
 * Two things separate them. A note the clinic wrote itself never went through
 * the intake and so never had a fee to settle, and an unpaid booking is only
 * worth flagging while the clinic is actually charging — `isFeeCharged` is
 * `tenant_info.patient_intake_fee_charged`, which says whether it is.
 *
 * Paid is reported either way: the money was taken, and the clinic switching
 * its fee off afterwards does not unmake that.
 *
 * A booking taken while the fee was on and then read after it was switched off
 * is the one case this gets wrong — it stops saying "unpaid" — which is the
 * cost of answering from the clinic's current setting rather than storing what
 * was due per booking.
 */
export const getPatientIntakeBookingFeeState = ({ note = {}, isFeeCharged = false } = {}) => {
	if (!note?.is_patient_intake) return "";
	if (note?.is_paid) return "paid";

	return isFeeCharged ? "unpaid" : "";
};

/**
 * Whether booking an appointment is the only way through this clinic's intake.
 *
 * A booking fee reserves an appointment slot, and a walk-in reserves none, so a
 * clinic charging one does not offer walking in. That leaves "choose your
 * booking" with a single button, and a step offering one choice is not a choice
 * — the steps ahead of it use this to send the patient straight to the calendar.
 *
 * The calendar has to be on for any of that to hold: without it there is no
 * appointment to book and walking in is the only way through, so nothing is
 * skipped and the fee is not charged.
 */
export const isPatientIntakeAppointmentOnly = ({ patientIntakeData = {} } = {}) =>
	Boolean(patientIntakeData?.enable_calendar_slot) && Boolean(patientIntakeData?.payment?.required);
